/**
 * ============================
 * Custom Order Core (共通ロジック)
 * ============================
 * Page 1 / Page 2 双方で読み込む。
 * ・localStorage による state 管理
 * ・詳細モーダル（× / 背景クリック / ESC で閉じる）
 * ・バリアント正引き・価格計算（合計 / 前金 / 残金）
 * ・データ getter
 * ・カート追加（/cart/add.js ＋ line_item_properties。規約は admin-schema §9-1）
 */

(function () {
  "use strict";

  const STATE_KEY = "customOrderState";

  const data = window.CustomOrderData || {
    categories: [],
    option_types: {},
    products: []
  };

  /* ============================
     State (localStorage)
  ============================ */
  function getState() {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function setState(partial) {
    const next = Object.assign({}, getState(), partial, {
      updatedAt: new Date().toISOString()
    });
    localStorage.setItem(STATE_KEY, JSON.stringify(next));
    return next;
  }

  function clearState() {
    localStorage.removeItem(STATE_KEY);
  }

  /* ============================
     Getters
  ============================ */
  function getProduct(id) {
    return (data.products || []).find((p) => p.id === id);
  }
  // オプション種別の定義（共通レジストリ）を取得
  function getOptionType(type) {
    return (data.option_types || {})[type] || null;
  }
  // 共通レジストリから選択肢を正引き（軸の種類に非依存）
  function getChoice(type, id) {
    const t = getOptionType(type);
    return t && t.choices ? t.choices[id] || null : null;
  }

  /* ============================
     Variant / Price
  ============================ */
  // pricing:"variant" の全軸について state.selections と variant.selections が
  // 一致する variant を探す（レザー×ファスナーでも襟×サイズでも同一ロジック）。
  function findVariant(state) {
    const product = getProduct(state.productId);
    if (!product) return null;

    const sel = (state && state.selections) || {};
    const variantGroups = (product.option_groups || []).filter(
      (g) => g.pricing === "variant"
    );

    return (product.variants || []).find((v) => {
      const vsel = v.selections || {};
      return variantGroups.every((g) => vsel[g.type] === sel[g.type]);
    });
  }

  /* ============================
     受注可否（A-7）
  ============================ */
  // variant.available は「その組合せを受注できるか」。未設定は true 扱い。
  // 在庫追跡 OFF 運用のため Shopify 標準の variant.available は使えない
  // （常に true になる）。管理画面のバリアントメタフィールド
  // custom_order.orderable がこの値の供給元（admin-schema §4-2）。
  function isVariantAvailable(variant) {
    return !!variant && variant.available !== false;
  }

  // ある選択肢を選んだとき、受注可能な variant が1つでも残るか。
  // 他の variant 軸のうち「すでに選ばれているもの」だけを条件に加える
  // （未選択の軸はまだ自由なので条件にしない）。これにより
  // 「レザーを選ぶ → その革で受注できないファスナーが落ちる」挙動になる。
  function isChoiceAvailable(product, group, choiceId, selections) {
    // 価格に関わらない軸（property / none）は受注可否の対象外
    if (!product || !group || group.pricing !== "variant") return true;
    // バリアントが1件も無いのはデータ不備。ここで全カードを潰すと
    // 原因が分からなくなるので、判定をスキップして通す。
    if (!(product.variants || []).length) return true;

    const sel = selections || {};
    const otherGroups = (product.option_groups || []).filter(
      (g) => g.pricing === "variant" && g.type !== group.type && sel[g.type]
    );

    return (product.variants || []).some((v) => {
      const vsel = v.selections || {};
      if (vsel[group.type] !== choiceId) return false;
      if (!otherGroups.every((g) => vsel[g.type] === sel[g.type])) return false;
      return isVariantAvailable(v);
    });
  }

  // その軸で「この選択肢を選ぶと、その商品でいくら上がるか」を実価格から求める。
  //
  // choice.extra_price を使わない理由：extra_price は選択肢そのものに紐づく
  // 共有の値なので、同じ革を使う商品でも差額が違う場合に嘘になる
  // （例：Waxed Horse Butt は Doctor Bag では +22,000 だが Folded Wallet では +0）。
  // 実際に売る値段はバリアント価格なので、そこから引くのが唯一ずれない。
  //
  // 基準はその軸の最安。他の variant 軸は「すでに選ばれているもの」だけ揃える
  // （isChoiceAvailable と同じ考え方。未選択の軸は自由なので条件にしない）。
  // 戻り値は合計金額ベースの差額。variant 軸以外・比較不能なときは null。
  function choiceExtraPrice(product, group, choiceId, selections) {
    if (!product || !group || group.pricing !== "variant") return null;

    const sel = selections || {};
    const otherGroups = (product.option_groups || []).filter(
      (g) => g.pricing === "variant" && g.type !== group.type && sel[g.type]
    );
    const variants = (product.variants || []).filter((v) => {
      const vsel = v.selections || {};
      return otherGroups.every((g) => vsel[g.type] === sel[g.type]);
    });
    if (!variants.length) return null;

    // 軸の選択肢ごとの最安（合計金額）
    const minByChoice = {};
    variants.forEach((v) => {
      const cid = (v.selections || {})[group.type];
      if (!cid) return;
      const total = calculatePrices(product, v).totalPrice;
      if (minByChoice[cid] === undefined || total < minByChoice[cid]) {
        minByChoice[cid] = total;
      }
    });

    const mine = minByChoice[choiceId];
    if (mine === undefined) return null;

    const base = Math.min.apply(null, Object.keys(minByChoice).map((k) => minByChoice[k]));
    return mine - base;
  }

  /* ============================
     必須選択（A-8 / PRD O-7）
  ============================ */
  // required（既定 true）なのに未選択の select 軸を返す。
  // 空配列＝カート追加の必須条件を満たしている。
  function missingRequiredGroups(product, selections) {
    const sel = selections || {};
    return (product.option_groups || []).filter(
      (g) => g.kind !== "info" && g.required !== false && !sel[g.type]
    );
  }

  // 合計・前金・残金を算出。
  //
  // ★ 合計金額（variant.total_price）が唯一の正で、前金 = 合計 × deposit_ratio%。
  // 逆算（前金 ÷ 比率）にしていると、比率を 50→30 に変えたときに
  // 「前金が下がる」のではなく「合計が上がる」という逆の結果になる。
  // 比率は合計をどう分割するかの設定なので、合計を正にするのが正しい向き。
  //
  // `variant.deposit_price` は **Shopify が決済時に実際に請求する額**（バリアント価格）。
  // 表示用の前金と一致していなければならない（ずれの検出は isPriceConsistent）。
  //
  // total_price 未設定のバリアントは従来方式（前金 ÷ 比率）で計算する。
  // 19件を少しずつ入力していく移行期間に、未入力のものが壊れないようにするため。
  function calculatePrices(product, variant) {
    const depositRatio = product.deposit_ratio || 50;
    const ratio = depositRatio / 100;
    const chargedPrice = variant.deposit_price; // 実際に請求される額
    const hasTotal =
      typeof variant.total_price === "number" && variant.total_price > 0;

    const totalPrice = hasTotal
      ? variant.total_price
      : Math.round(chargedPrice / ratio);
    const depositPrice = hasTotal
      ? Math.round(totalPrice * ratio)
      : chargedPrice;

    return {
      totalPrice: totalPrice,
      depositPrice: depositPrice,
      balancePrice: totalPrice - depositPrice,
      chargedPrice: chargedPrice,
      hasTotal: hasTotal,
      // 注文プロパティの見出し「前金（50%）」に使う。％表記なので生の整数のまま返す。
      depositRatio: depositRatio
    };
  }

  // 画面に出す前金と、Shopify が実際に請求する額が一致しているか。
  // 合計額の入力ミスや比率変更の反映漏れでここがずれると、
  // 「画面には ¥45,000 と出ているのに ¥22,500 しか請求されない」等の事故になる。
  // ずれているあいだはカートに進ませない（page2.js の updateCartButton）。
  function isPriceConsistent(product, variant) {
    if (!product || !variant) return false;
    const p = calculatePrices(product, variant);
    return p.depositPrice === p.chargedPrice;
  }

  function formatYen(n) {
    return "¥" + Number(n).toLocaleString();
  }

  /* ============================
     表示テキスト
  ============================ */
  // 確認明細と line_item_properties の両方がこれを使う。
  // 注文に載る文言と画面の文言を一致させるため、生成は必ずここ1箇所に置く
  // （admin-schema §9-1：注文詳細・確認メールにそのまま印字される）。
  function groupLabel(group) {
    const typeDef = group.type ? getOptionType(group.type) : null;
    return group.label || (typeDef && typeDef.label) || group.type || "";
  }

  function choiceDisplayName(choice) {
    if (choice.meta_label) return choice.name + "（" + choice.meta_label + "）";
    return choice.name;
  }

  /* ============================
     Modal（DOM 動的生成）
  ============================ */
  let modalEl = null;
  // モーダルを開く前にフォーカスがあった要素。閉じたらここへ戻す（A-8 / NFR6）
  let lastFocusedEl = null;
  // 現在開いているカルーセルの再センタリング関数。リサイズ時にこれだけ呼び直す
  let carouselRecenter = null;

  window.addEventListener("resize", () => {
    if (carouselRecenter) carouselRecenter();
  });

  const FOCUSABLE =
    'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled),' +
    ' textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

  function focusableIn(el) {
    // display:none の中身は拾わない（画像が1枚のときのカルーセル操作ボタン等）
    return Array.prototype.filter.call(
      el.querySelectorAll(FOCUSABLE),
      (n) => n.offsetWidth > 0 || n.offsetHeight > 0
    );
  }

  function ensureModal() {
    if (modalEl) return modalEl;

    modalEl = document.createElement("div");
    modalEl.className = "co-modal";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.innerHTML =
      '<div class="co-modal__backdrop" data-co-close></div>' +
      '<div class="co-modal__panel" role="dialog" aria-modal="true" aria-labelledby="co-modal-title">' +
      '  <button class="co-modal__close" type="button" data-co-close aria-label="閉じる">&times;</button>' +
      '  <h3 class="co-modal__title" id="co-modal-title"></h3>' +
      '  <p class="co-modal__price"></p>' +
      '  <div class="co-modal__images"></div>' +
      '  <p class="co-modal__desc"></p>' +
      "</div>";

    document.body.appendChild(modalEl);

    modalEl.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-co-close")) closeModal();
    });

    // フォーカストラップ：開いている間は Tab がモーダルの外へ出ない
    modalEl.addEventListener("keydown", (e) => {
      if (e.key !== "Tab") return;
      const items = focusableIn(modalEl);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    return modalEl;
  }

  // 画像 URL は innerHTML に連結せず、生成後に src プロパティで入れる
  // （管理画面由来の値をマークアップとして解釈させないため。PRD §14 本番硬化）
  function applyImageSources(mount, images) {
    const imgEls = mount.querySelectorAll(".co-modal__img");
    images.forEach((src, i) => {
      if (imgEls[i]) imgEls[i].src = src;
    });
  }

  function buildCarousel(mount, images) {
    images = (images || []).filter(Boolean);
    carouselRecenter = null;

    if (images.length <= 1) {
      mount.innerHTML = images.length
        ? '<img alt="" class="co-modal__img" />'
        : "";
      applyImageSources(mount, images);
      return;
    }

    const slides = images
      .map(
        () =>
          '<div class="co-carousel__slide">' +
          '<img alt="" class="co-modal__img" />' +
          "</div>"
      )
      .join("");

    const dots = images
      .map(
        (_, i) =>
          '<button type="button" class="co-carousel__dot' +
          (i === 0 ? " is-active" : "") +
          '" data-idx="' +
          i +
          '" aria-label="' +
          (i + 1) +
          '枚目"></button>'
      )
      .join("");

    mount.innerHTML =
      '<div class="co-carousel">' +
      '  <div class="co-carousel__viewport">' +
      '    <div class="co-carousel__track">' + slides + "</div>" +
      "  </div>" +
      '  <button type="button" class="co-carousel__nav co-carousel__nav--prev" aria-label="前の画像">&#8249;</button>' +
      '  <button type="button" class="co-carousel__nav co-carousel__nav--next" aria-label="次の画像">&#8250;</button>' +
      '  <div class="co-carousel__dots">' + dots + "</div>" +
      "</div>";

    applyImageSources(mount, images);

    const viewport = mount.querySelector(".co-carousel__viewport");
    const track = mount.querySelector(".co-carousel__track");
    const dotEls = Array.prototype.slice.call(
      mount.querySelectorAll(".co-carousel__dot")
    );
    let index = 0;

    // 正方形化した画像は横幅に余りが出るため、選択中の画像をビューポート中央に
    // 揃える形でオフセットを計算する（余った分＝前後の画像がのぞく）
    function render() {
      const slideEl = mount.querySelector(".co-carousel__slide");
      if (slideEl) {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const slideWidth = slideEl.getBoundingClientRect().width;
        const step = slideWidth + gap;
        const offset =
          viewport.clientWidth / 2 - (index * step + slideWidth / 2);
        track.style.transform = "translateX(" + offset + "px)";
      }
      dotEls.forEach((d, i) => d.classList.toggle("is-active", i === index));
    }
    function goTo(i) {
      index = Math.max(0, Math.min(images.length - 1, i));
      render();
    }
    carouselRecenter = render;

    mount
      .querySelector(".co-carousel__nav--prev")
      .addEventListener("click", () => goTo(index - 1));
    mount
      .querySelector(".co-carousel__nav--next")
      .addEventListener("click", () => goTo(index + 1));
    dotEls.forEach((d) =>
      d.addEventListener("click", () =>
        goTo(parseInt(d.getAttribute("data-idx"), 10))
      )
    );

    // タッチ / スワイプ操作
    let startX = null;
    viewport.addEventListener(
      "touchstart",
      (e) => {
        startX = e.touches[0].clientX;
      },
      { passive: true }
    );
    viewport.addEventListener("touchend", (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) goTo(index + (dx < 0 ? 1 : -1));
      startX = null;
    });

    render();
  }

  function openModal(opts) {
    const el = ensureModal();
    const images = opts.images || [];

    lastFocusedEl = document.activeElement;

    el.querySelector(".co-modal__title").textContent = opts.title || "";

    const priceEl = el.querySelector(".co-modal__price");
    if (opts.price != null && opts.price !== "") {
      priceEl.textContent = opts.price;
      priceEl.style.display = "";
    } else {
      priceEl.style.display = "none";
    }

    el.querySelector(".co-modal__desc").textContent = opts.description || "";

    el.classList.add("is-open");
    el.setAttribute("aria-hidden", "false");
    document.body.classList.add("co-modal-open");

    // 幅を正しく測るため、表示状態にしてからカルーセルを構築する
    buildCarousel(el.querySelector(".co-modal__images"), images);

    // 開いたらモーダル内へフォーカスを移す（キーボードだけで閉じられるように）
    const closeBtn = el.querySelector(".co-modal__close");
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!modalEl || !modalEl.classList.contains("is-open")) return;
    modalEl.classList.remove("is-open");
    modalEl.setAttribute("aria-hidden", "true");
    document.body.classList.remove("co-modal-open");
    carouselRecenter = null;

    // 開く前の位置へフォーカスを戻す。要素が消えている場合は何もしない
    if (lastFocusedEl && document.contains(lastFocusedEl)) {
      lastFocusedEl.focus();
    }
    lastFocusedEl = null;
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeModal();
  });

  /* ============================
     Cart
  ============================ */
  // 集計用プロパティのキー。custom.js の同名定数と必ず一致させること
  // （custom.js はカートページで core.js 無しに動くので、定数を共有できない）。
  const PROP_TOTAL_RAW = "_合計金額_円";
  const PROP_BALANCE_RAW = "_残金_円";

  // 注文に残す line_item_properties（規約は admin-schema §9-1）。
  //   キー＝軸の表示名、値＝選択肢の表示名。handle は照合キーであって表示物ではない。
  //   pricing:"variant" の軸はバリアント自体が記録するので出さない（二重記録の防止）。
  //   未選択の required:false 軸はキーごと出さない（空のプロパティを注文に並べない）。
  function buildLineItemProperties(product, variant, selections) {
    const sel = selections || {};
    const props = {};

    (product.option_groups || []).forEach((group) => {
      if (group.kind === "info" || group.pricing !== "property") return;
      const choice = getChoice(group.type, sel[group.type]);
      if (!choice) return;
      props[groupLabel(group)] = choiceDisplayName(choice);
    });

    // 前金だけがバリアント価格なので、合計と残金は注文側に文字で残さないと追えない
    const p = calculatePrices(product, variant);
    props["合計金額"] = formatYen(p.totalPrice);
    props["前金（" + p.depositRatio + "%）"] = formatYen(p.depositPrice);
    props["残金（後日ご請求）"] = formatYen(p.balancePrice);

    // 集計用の生値（1点あたり・円）。`_` 始まりなので顧客には出ず、
    // 注文の「追加詳細」を作る custom.js と Shopify Flow だけが読む。
    // 表示用の "¥32,500" を後からパースすると通貨記号や桁区切りに引きずられるため、
    // 数値は数値のまま別に持たせる（§9-1 の付随ルール6）。
    //
    // ★ 残金は「合計 − chargedPrice」で出す。balancePrice（表示用の前金基準）ではない。
    // 通常この2つは一致するが、価格入力ミスで前金と請求額がずれている場合、
    // 後日請求すべき残金は **実際に請求された額** を引いた残りになる。
    // isPriceConsistent はボタンを止めるだけで addToCart 自体は素通りするため、
    // ここで請求額基準にしておかないと、事故時に請求不足・過剰請求が起きる。
    props[PROP_TOTAL_RAW] = String(p.totalPrice);
    props[PROP_BALANCE_RAW] = String(p.totalPrice - p.chargedPrice);

    return props;
  }

  // 前金額のバリアントを1点カートに入れる。成功したら true。
  // 遷移は呼び出し側（page2.js）の責任にする（O-8 の遷移先が未決定のため）。
  async function addToCart(product, variant, selections) {
    if (!product || !variant) return false;

    // ダミーデータの variant_id は 0。実データに切り替わるまでここで止める。
    if (!variant.variant_id) {
      console.error("[CustomOrder] variant_id がありません。カートに追加できません。");
      return false;
    }

    let res;
    try {
      res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          items: [
            {
              id: variant.variant_id,
              quantity: 1,
              properties: buildLineItemProperties(product, variant, selections)
            }
          ]
        })
      });
    } catch (e) {
      console.error("[CustomOrder] カートへの通信に失敗しました。", e);
      return false;
    }

    if (!res.ok) {
      // Shopify は在庫切れ・非公開などを 4xx ＋ description で返す。
      // 販売チャネルからの除外漏れもここに出るので、本番調査の第一歩になる。
      let detail = "";
      try {
        const body = await res.json();
        detail = body.description || body.message || "";
      } catch (e) {
        /* JSON でない場合は detail 無しで報告する */
      }
      console.error("[CustomOrder] カート追加に失敗しました。", res.status, detail);
      return false;
    }

    // 注文レベルの合計・前金・残金（＝管理画面の「追加詳細」）を更新する。
    // ここで待たないと、直後の /cart 遷移でリクエストが打ち切られることがある。
    // 失敗してもカート追加自体は成立しているので true は返す（明細側に
    // 金額は残っており、追加詳細はカートページ表示時に貼り直される）。
    if (window.IeribCart && window.IeribCart.syncCustomOrderTotals) {
      await window.IeribCart.syncCustomOrderTotals();
    }

    return true;
  }

  /* ============================
     Export
  ============================ */
  window.CustomOrder = {
    data: data,
    getState: getState,
    setState: setState,
    clearState: clearState,
    getProduct: getProduct,
    getOptionType: getOptionType,
    getChoice: getChoice,
    findVariant: findVariant,
    isVariantAvailable: isVariantAvailable,
    isChoiceAvailable: isChoiceAvailable,
    choiceExtraPrice: choiceExtraPrice,
    missingRequiredGroups: missingRequiredGroups,
    calculatePrices: calculatePrices,
    isPriceConsistent: isPriceConsistent,
    formatYen: formatYen,
    groupLabel: groupLabel,
    choiceDisplayName: choiceDisplayName,
    openModal: openModal,
    closeModal: closeModal,
    addToCart: addToCart
  };
})();
